import { Outlet } from "react-router-dom";
import { VideoSignalAutoHide } from "../../../../../components/utils/VideoSignalAutoHide";
import { PageMasterMode, usePageContext } from "../../../../../components/navigation/PageContext";

import {
  compareMeetingSessionRoles,
  isSpectator,
  mediaSessionIsHidden,
  MeetingSessionOvenMediaReceiver,
  useAuthContext,
  useMeetingAudioMixer,
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  AudioMixerOptions,
  awaitWithMinimumDelay,
  MediaCaptureRef,
  useRefAutoUpdate
} from "@kalyzee/kast-web-sdk/web-components";
import { MeetingSessionHighlightingState, MeetingState, MeetingUpdateByIdResult } from "@kalyzee/kast-web-sdk/websocket";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError, toastSuccess } from "../../../../../helpers/toast";
import { MeetingSourceExtended, useMeetingDataOnChange, useMeetingSources } from "../../../../../hooks/meeting";

import { DEFAULT_AUDIO_MATCH } from "../../../../../helpers/defaultMatchers";
import { matchEngine } from "../../../../../helpers/matchEngine";
import { isExpertVideoSession } from "../../../../../helpers/meeting";
import { NavigateGlobalQuery } from "../../../../../helpers/navigation";

import PauseView from "../../../../../components/utils/PauseView";
import { useSettings } from "../../../../../hooks/settings";

import { BottomPanelWebrtcSessionStats } from "../../../../../components/debug/BottonPanelWebrtcSessionStats";
import { DevSoundIndicatorsBanner } from "../../../../../components/utils/DevSoundIndicatorBanner";

import { BoxOverlay, BoxOverlayProps, OverlayContainer, Button, PrimaryButton, ReactIcons, RevealContainer } from "@kalyzee/kalyzee-ui";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { MasterUI } from "../../../../../components/common/MasterUI";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../../components/common/MediaSourcePicker";
import { MediaSourceWorkBox } from "../../../../../components/workBoxes/MediaSourceWorkBox";
import { ShareWorkBox } from "../../../../../components/workBoxes/ShareWorkBox";
import { SessionMode } from "../../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../../contexts/appStore/selectors";
import { getAudioContextForAudioMixer } from "../../../../../helpers/audio";
import { diagLogger } from "../../../../../helpers/mediaDiagnostics";
import { addPopup, removePopup, removePopupByGroup } from "../../../../../helpers/popup";
import { isExpert } from "../../../../../helpers/roles";
import { sessionIsEphemeral } from "../../../../../helpers/session";
import { getTogglableMedias, togglableMediasEnabled } from "../../../../../helpers/screenShare";

import clsx from "clsx";
import { LoadingView } from "../../../../../components/utils/LoadingView";
import { useAppStoreDispatch } from "../../../../../contexts/appStore/context";
import { useMeetingUtilsContext } from "../../../../../contexts/meetingUtils/context";
import { useBoardMedia } from "../../../../../hooks/board";
import { useMeetingMasterMessagingBridge } from "../../../../../hooks/secondaryScreens/messagingBridge";

import { BottomBar } from "../../../../../components/common/BottomBar";
import { CurrentWorkBox } from "../../../../../components/common/CurrentWorkBox";
import { HighlightPopup } from "../../../../../components/common/HighlightPopup";
import { SideBar } from "../../../../../components/common/SideBar";
import { SurveyBar } from "../../../../../components/common/SurveyBar";
import { useSdkContext } from "../../../../../contexts/sdk/context";
import { useFeatures } from "../../../../../hooks/features";
import { useHighlightNotifications } from "../../../../../hooks/highlight";
import { useScreenShare } from "../../../../../hooks/screenShare";
import styles from "./base.module.css";

export const ConnectedMeetingPage = () => {
  const [settings] = useSettings();
  const disableMeetingList = settings?.ui?.disableMeetingList === true;

  const features = useFeatures();

  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const sdkCtx = useSdkContext();

  const selfUser = useAppStoreSelector(state => state.user);
  const { logout } = useAuthContext();
  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const outputAudioDeviceInfo = useAppStoreSelector(state => state.outputAudioDeviceInfo);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);
  const workBoxOpen = useAppStoreSelector(state => state.workBoxOpen);

  const dispatch = useAppStoreDispatch();

  const { containerAPI, populated } = useMeetingUtilsContext();
  const containerAPIRef = useRefAutoUpdate(containerAPI);

  const [showEndMeetingConfirmationPopup, setShowEndMeetingConfirmationPopup] = useState(false);

  const [meeting] = useMeetingSelector(containerAPI, "meeting");
  const [session] = useMeetingSelector(containerAPI, "session");
  const sessionRef = useRefAutoUpdate(session);
  const [sessions] = useMeetingSelector(containerAPI, "sessions")
  const [rooms] = useMeetingSelector(containerAPI, "rooms");
  const [emissionSessions] = useMeetingSelector(containerAPI, "emissionSessions");
  const [receptionSessions] = useMeetingSelector(containerAPI, "receptionSessions");
  const [chatrooms] = useMeetingSelector(containerAPI, "chatrooms");

  const mediaSelectorRef = useRef<MediaCaptureRef>();

  const [chatAdapters] = useMeetingSelector(containerAPI, "chatAdapters");
  const chatInfo = useMemo(() => {
    const room = chatrooms?.[0];
    if (!room) return undefined;
    const adapter = chatAdapters?.[room.id];
    return { room, adapter };
  }, [chatrooms, chatAdapters]);

  const [unreadMessagesNb, setUnreadMessagesNb] = useState(0);
  useEffect(() => {
    const handle = chatInfo?.adapter?.addEventListener("messages", (messages) => {
      setUnreadMessagesNb(messages.filter(m => !m.read).length);
    });
    return () => handle?.flush();
  }, [chatInfo?.adapter]);

  const isStoppingRef = useRef(false);

  // True as soon as we observe the meeting in a non-stopped state during this
  // mount. Lets us tell "the course ended while I was watching" (auto-redirect
  // countdown) from "I arrived on an already-finished course" (hold on a
  // waiting screen, no auto-redirect).
  const witnessedMeetingLiveRef = useRef(false);

  const sources = useMeetingSources();

  // When the previous page navigated here with `state.openShare`
  // (e.g. CreateMeetingPopup after creating + joining), auto-open the
  // share popup so the creator can immediately share the link. We
  // consume the state by replacing the location with `state: null`,
  // which prevents re-triggering on refresh.
  useEffect(() => {
    const ctx = pageContextRef.current;
    const state = ctx?.location?.state as { openShare?: boolean } | null | undefined;
    if (!state?.openShare) return;
    dispatch("setPopupMode", "share");
    window.history.replaceState(null, "", window.location.href);
  }, [dispatch]);

  // Handles all the notifications
  // and the media selection popup for highlighting
  const highlightMediaSelection = useHighlightNotifications();

  const requestingHighlight = highlightMediaSelection.requestingHighlight;

  // Held at the page level: the bottom bar is remounted on each layout switch.
  const screenShare = useScreenShare();

  const usableSources: MeetingSourceExtended[] = useMemo(() => {
    if (!isSpectator(session?.role)) return (sources ?? []) as any;
    if (session?.highlighting === MeetingSessionHighlightingState.ONGOING) return (sources ?? []) as any;
    if (session?.highlighting === MeetingSessionHighlightingState.PENDING_REQUEST) return (sources ?? []) as any;
    if (requestingHighlight) return (sources ?? []) as any;
    return [];
  }, [sources, session?.role, session?.highlighting, requestingHighlight]);

  const boardLocalOrRemoteMedia = useBoardMedia();

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting?.id) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    // A media hidden at the source is not shown at all: its video track is
    // muted on both sides, a player would only display a dead frame.
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => (
      !enabledVideoSessionsIds?.includes(media.mediaSession.id) && !mediaSessionIsHidden(media.mediaSession)
    ));

    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  useMeetingMasterMessagingBridge();

  useMeetingDataOnChange(
    containerAPI,
    (state) => state?.session?.permissions?.audio ?? true,
    (value, previous) => {
      if (value) toastSuccess("Vous pouvez à nouveau activer votre micro.");
      else toastError("L'activation du micro est désormais bloquée.");
    }
  );

  // Passive diagnostic: meeting-level permissions gate the camera with no UI
  // feedback (only the audio one has a toast above).
  useMeetingDataOnChange(
    containerAPI,
    (state) => JSON.stringify(state?.session?.permissions ?? {}),
    (value, previous) => {
      diagLogger.log("session.permissions changed", { previous, current: value });
    }
  );

  useEffect(() => {
    const emissionManager = containerAPI?.emissionManager;
    if (emissionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (emissionManager.paused) emissionManager.restore();
      } else {
        if (!emissionManager.paused) emissionManager.pause();
      }
    }
    const receptionManager = containerAPI?.receptionManager;
    if (receptionManager) {
      if (meeting?.state === MeetingState.RUNNING) {
        if (receptionManager.paused) receptionManager.restore();
      } else {
        if (!receptionManager.paused) receptionManager.pause();
      }
    }
  }, [containerAPI, meeting?.state])

  ////////////////////// Work Boxes DATA //////////////////////

  const audioMatchRoleComparator = useMemo(() => ({ "session.role": (a: any, b: any) => -compareMeetingSessionRoles(a, b) }), []);

  const canHearSession = useCallback((_: MeetingContainerAPI, receiverSession: MeetingSessionOvenMediaReceiver) => {
    const freshSession = sessions?.find(s => s.id === receiverSession.data.session.id);
    const freshMedia = freshSession?.medias.find(m => m.id === receiverSession.data.mediaSession.id);
    if (!freshSession || !freshMedia) return false;
    const highlighted = populated.activity?.mode === "highlight"
      && populated.activity.confirmedSessions.some(cs => cs.session.id === freshSession.id);
    return matchEngine(
      { session: freshSession, media: freshMedia, currentSession: session, highlighted: !!highlighted },
      features.audioMatch || DEFAULT_AUDIO_MATCH,
      audioMatchRoleComparator,
    );
  }, [features.audioMatch, audioMatchRoleComparator, populated.activity, session, sessions]);

  const audioMixerOptions: AudioMixerOptions = useMemo(() => ({
    ...(settings.debug?.audio?.mixer ?? {}),
    forceEnableDecoderHack: true,
  }), [settings.debug?.audio?.mixer]);

  const audioMixer = useMeetingAudioMixer(
    getAudioContextForAudioMixer(),
    containerAPI,
    true,
    canHearSession,
    audioMixerOptions
  );

  // ---------------------------------------------------------------------- //
  // ---------------------------   UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const updateMedias = useCallback(async () => {
    const meetingContainerAPI = containerAPIRef.current;
    if (!meetingContainerAPI) return;

    const manager = meetingContainerAPI.emissionManager;

    if (session) {
      session.medias?.forEach((m) => {
        if (!m.video.enabled && manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.disableVideo({ mediaSessionId: m.id });
        } else if (m.video.enabled && !manager.videoIsEnabled({ mediaSessionId: m.id })) {
          manager.enableVideo({ mediaSessionId: m.id });
        }
        if (!m.audio.enabled && manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.disableAudio({ mediaSessionId: m.id });
        } else if (m.audio.enabled && !manager.audioIsEnabled({ mediaSessionId: m.id })) {
          manager.enableAudio({ mediaSessionId: m.id });
        }
      });
    }

    const state = meeting?.state;
    if (state === MeetingState.PAUSED) {
      audioMixer?.suspend().catch((err) => console.error("Error suspend audio : ", err));
    } else {
      audioMixer?.resume().catch((err) => console.error("Error resume audio : ", err));
    }

    // No media on the session yet: the manager has no emission session and
    // would report its defaults (true), overriding the user's toggles. The
    // screen share does not count: it stays on whatever the toggles say.
    if (!session || !getTogglableMedias(session).length) return;

    dispatch("setAudioStreamEnabled", togglableMediasEnabled(manager, session, "audio"));
    dispatch("setVideoStreamEnabled", togglableMediasEnabled(manager, session, "video"));
  }, [dispatch, session, meeting]);

  // ------------------ EXPERT UTILS ------------------ //

  const updateMeetingState = useCallback(async (state: MeetingState) => {
    pageContextRef.current?.setLoading(true);
    let result: MeetingUpdateByIdResult | undefined;
    try {
      result = await containerAPIRef.current?.updateMeetingState(state);
    } catch (err) {
    } finally {
      pageContextRef.current?.setLoading(false);
    }
    return result;
  }, []);

  const start = async (): Promise<boolean> => {
    isStoppingRef.current = true;
    pageContextRef.current?.setLoading(true);
    const result = await awaitWithMinimumDelay(updateMeetingState(MeetingState.RUNNING), 500);
    pageContextRef.current?.setLoading(false);
    if (result && !result.error && result.response?.meeting?.state === MeetingState.RUNNING) {
      return true;
    }
    isStoppingRef.current = false;
    toastError("Une erreur est apparue.");
    return false;
  };

  const onPause = useCallback(async () => {
    updateMeetingState(MeetingState.PAUSED);
  }, []);

  const onStop = useCallback(async () => {
    setShowEndMeetingConfirmationPopup(true);
  }, []);

  const onResume = useCallback(async () => {
    updateMeetingState(MeetingState.RUNNING);
  }, []);

  // ---------------------------------------------------------------------- //
  // --------------------------- EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  // An expert arriving on an IDLE course launches it automatically (instant
  // meetings are created IDLE and should start without an extra click);
  // non-experts wait on the "course not started" popup. One-shot — if the
  // launch fails, `autoStartPending` drops and the popup shows with its
  // manual launch button.
  const autoStartCheckedRef = useRef(false);
  const [autoStartPending, setAutoStartPending] = useState(true);
  useEffect(() => {
    if (autoStartCheckedRef.current) return;
    if (meeting?.state !== MeetingState.IDLE) return;
    if (!session) return; // wait until we know our role
    autoStartCheckedRef.current = true;
    if (!isExpert(session)) {
      setAutoStartPending(false);
      return;
    }
    start().finally(() => setAutoStartPending(false));
  }, [meeting?.state, session]);

  useEffect(() => {
    if (!meeting) return;

    if (meeting.specializedData?.isPedagogy) {
      if (pageContextRef.current?.page === PageMasterMode.MEETING_PEDAGOGY) {
        return;
      }
      pageContextRef.current?.goTo(PageMasterMode.MEETING_PEDAGOGY, { id: pageContextRef.current?.params?.id });
      return;
    }

    if (pageContextRef.current?.page !== PageMasterMode.MEETING_DASHBOARD) {
      pageContextRef.current?.goTo(PageMasterMode.MEETING_DASHBOARD, { id: pageContextRef.current?.params?.id });
    }
  }, [pageContextRef]);

  useEffect(() => {
    if (!mediaSelectorRef.current) return;
    mediaSelectorRef.current.enableAudio(audioEnabled);
    mediaSelectorRef.current.enableVideo(videoEnabled);
  }, [audioEnabled, videoEnabled]);

  useEffect(() => {
    if (outputAudioDeviceInfo && audioMixer?.isRunning()) {
      audioMixer.setAudioOutputDevice(outputAudioDeviceInfo).catch(() => {
        console.error(`audio device (${JSON.stringify(outputAudioDeviceInfo)}) is invalid`);
      });
    }
  }, [audioMixer, outputAudioDeviceInfo]);

  // Expose the mixed classroom audio to the retransmission manager as the
  // 'audio-mixer' custom source (non-destructive tap). Destinations whose match
  // targets `type = 'custom' AND key = 'audio-mixer'` can then restream it; the
  // extractor alone decides whether/where it actually publishes.
  useEffect(() => {
    const retransmissionManager = containerAPI?.retransmissionManager;
    if (!retransmissionManager || !audioMixer) return;
    const handle = retransmissionManager.attachAudioMixer(audioMixer);
    return () => handle.detach();
  }, [containerAPI, audioMixer]);

  useEffect(() => {
    if (!emissionSessions) return;
    emissionSessions.forEach((s) => {
      const type = s.data.mediaSession.extra?.type;
      const group = type === "camera" ? "cameras" : type === "screen" ? "screens" : "undefined";
      if (group && (!s.data.groups || s.data.groups[0] !== group || s.data.groups.length !== 1)) {
        s.data.groups = [group];
      }
    });
  }, [emissionSessions]);

  useEffect(() => {
    if (!receptionSessions) return;
    receptionSessions.forEach((s) => {
      const isExpertVideo = isExpertVideoSession(meeting, s);
      if (s.data.extra?.video !== isExpertVideo || s.data.extra?.audio !== true) {
        s.data.extra = {
          video: isExpertVideo,
          audio: true,
        };
      }
    });
  }, [receptionSessions, meeting]);

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    if (settings.debug?.audio?.mixer?.public) (window as any).audioMixer = audioMixer;
    if (settings.debug?.meetingContext?.public) (window as any).meetingContext = containerAPI;
  }, [settings, audioMixer, containerAPI]);

  // ------------------ EXPERT EFFECTS ------------------ //

  useEffect(() => {
    const state = meeting?.state;

    // Record that we saw the meeting live so a later STOPPED counts as
    // "ended while present" rather than "arrived after it finished".
    if (state && state !== MeetingState.STOPPED && state !== MeetingState.IDLE) {
      witnessedMeetingLiveRef.current = true;
    }

    let currentPopup: React.ReactNode;
    const endMeetingPopup = () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }

      const notRunning = state === MeetingState.STOPPED || state === MeetingState.IDLE;

      // We only auto-redirect (countdown) when the course ends while the user
      // is present. Arriving on a course that hasn't started (IDLE) or is
      // already stopped holds on a waiting screen and lets the user leave
      // (or launch it, for experts) on their own.
      const waitingForStart = notRunning && !witnessedMeetingLiveRef.current;

      let buttons: BoxOverlayProps["buttons"];

      if (notRunning) {
        buttons = [
          {
            // Ephemeral (guest) sessions have no meeting list — leaving drops
            // the session (see the back-button handler in MasterTopBar).
            label: sessionIsEphemeral() || disableMeetingList ? "Sortir du meeting" : "Revenir à la liste des cours",
            onClick: () => {
              removePopup(currentPopup);
              removePopupByGroup("main");
              if (sessionIsEphemeral()) {
                // Clearing tokens swaps to the public tree, which redirects the
                // current /meeting/:id URL to the meeting lobby.
                logout();
              } else if (disableMeetingList) {
                // No-list mode: land on the lobby of the (over) meeting,
                // replacing the entry so no back can re-enter it. Same call
                // shape as the "exclude" handler below.
                const id = pageContextRef.current?.params?.id;
                if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true, forwardParams: [NavigateGlobalQuery.PROVIDER] });
                else pageContextRef.current?.goTo(PageMasterMode.LIST);
              } else {
                pageContextRef.current?.goTo(PageMasterMode.LIST);
              }
            },
            // Auto-redirect only when we witnessed the course end live.
            ...(waitingForStart ? {} : { validateAfter: 10 }),
          }
        ];
        if (isExpert(session)) {
          buttons.push({
            label: state === MeetingState.IDLE ? "Lancer le cours" : "Relancer le cours",
            onClick: async () => {
              await start();
              removePopup(currentPopup);
            }
          });
        }
      } else {
        buttons = [
          {
            label: "Terminer le cours",
            onClick: async () => {
              await updateMeetingState(MeetingState.STOPPED);
            }
          },
          {
            label: "Poursuivre le cours",
            onClick: () => {
              setShowEndMeetingConfirmationPopup(false);
            }
          }
        ];
      }

      currentPopup = (
        <OverlayContainer
          visible
          style={{
            width: "100dvw",
            height: "100dvh",
          }}
          overlay={
            <BoxOverlay
              title={
                !notRunning
                  ? "Voulez-vous vraiment terminer le cours ?"
                  : waitingForStart
                    ? "En attente du début du cours"
                    : state === MeetingState.STOPPED
                      ? "Le cours est terminé"
                      : "Le cours n'a pas commencé"
              }
              level={!notRunning ? "question" : waitingForStart ? "info" : "success"}
              buttons={buttons}
            />
          }
        />
      );

      addPopup(currentPopup, "meeting_state_stopped", "main");
    };

    if (state === MeetingState.STOPPED) {
      setShowEndMeetingConfirmationPopup(false);
    }

    // While an auto-start is pending (instant meeting creation flow), hold off
    // the "course not started" popup — it would only flash under the loading
    // overlay before the state flips to RUNNING.
    if ((state === MeetingState.IDLE && !autoStartPending) || state === MeetingState.STOPPED || showEndMeetingConfirmationPopup) {
      endMeetingPopup();
    }

    return () => {
      if (currentPopup) {
        removePopup(currentPopup);
        currentPopup = undefined;
      }
    };
  }, [meeting?.state, showEndMeetingConfirmationPopup, logout, disableMeetingList, autoStartPending]);

  useEffect(() => {
    const listener = containerAPI?.addEventListener("exclude", (event) => {
      if (event?.session?.id !== sessionRef.current?.id) return;
      if (event?.reason === "multi_connexion") {
        const popup = (
          <OverlayContainer
            visible
            style={{ width: "100dvw", height: "100dvh" }}
            overlay={
              <BoxOverlay
                title="Connexion transférée sur un autre appareil"
                level="info"
                buttons={[
                  { label: "OK", onClick: () => removePopup(popup) },
                ]}
              />
            }
          />
        );
        addPopup(popup, "student_exclude_multi_connexion", "main");
      } else {
        // Shown over the lobby we land on, with the excluder's message if any.
        const popup = (
          <OverlayContainer
            visible
            style={{ width: "100dvw", height: "100dvh" }}
            overlay={
              <BoxOverlay
                title={
                  <>
                    Vous avez été exclu(e) du meeting
                    {event?.message && <p className={styles.excludeMessage}>{event.message}</p>}
                  </>
                }
                level="info"
                buttons={[
                  { label: "OK", onClick: () => removePopup(popup) },
                ]}
              />
            }
          />
        );
        addPopup(popup, "student_exclude_message", "main");
      }
      const id = pageContextRef.current?.params?.id;
      // The lobby needs `provider` to resolve provider-scoped ids (defensive:
      // a connected meeting URL usually carries a plain mongo id).
      if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true, forwardParams: [NavigateGlobalQuery.PROVIDER] });
      // BACK_REFAC
      else pageContextRef.current?.goTo(PageMasterMode.LIST);
    });
    return () => listener?.flush();
  }, [containerAPI]);

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const workBoxMode = useAppStoreSelector(state => state.workBoxMode);

  const popupMode = useAppStoreSelector(state => state.popupMode);
  const sourcePickerOpen = popupMode === "sources";
  const shareOpen = popupMode === "share";

  useEffect(() => {
    if (isMobile) {
      dispatch("setWorkBoxOpen", false);
    }
  }, [isMobile]);

  const highlightRequestsNb = populated.highlighted.requesting.length;

  type MobileWorkBoxMode = "inputs" | "survey" | "highlight" | "chat";
  const mobileWorkBoxTabs = useMemo(() => {
    const iconStyle = { width: 18, height: 18 };
    const tabs: { mode: MobileWorkBoxMode; icon: React.ReactNode; notificationCount?: number }[] = [
      { mode: "inputs", icon: <ReactIcons.ComputerScreen style={iconStyle} /> },
    ];
    if (features.survey) tabs.push({ mode: "survey", icon: <ReactIcons.Stats style={iconStyle} /> });
    if (isExpert(session) && features.board) tabs.push({ mode: "highlight", icon: <ReactIcons.Hand2 style={iconStyle} />, notificationCount: highlightRequestsNb || undefined });
    // Only show the chat tab when a chat is associated with the meeting.
    if (chatInfo?.adapter) tabs.push({ mode: "chat", icon: <ReactIcons.ChatBubble style={iconStyle} />, notificationCount: unreadMessagesNb || undefined });
    return tabs;
  }, [features.survey, features.board, session, highlightRequestsNb, unreadMessagesNb, chatInfo?.adapter]);

  const handleMobileTabClick = useCallback((mode: MobileWorkBoxMode) => {
    dispatch("setWorkBoxMode", mode);
  }, [dispatch]);

  const closeWorkBoxOverlay = useCallback(() => {
    dispatch("setWorkBoxOpen", false);
  }, [dispatch]);

  const openSourcesOverlay = useCallback(() => {
    dispatch("setPopupMode", "sources");
  }, [dispatch]);

  const closeSourcesOverlay = useCallback(() => {
    dispatch("setPopupMode", undefined);
  }, [dispatch]);

  const closeShareOverlay = useCallback(() => {
    dispatch("setPopupMode", undefined);
  }, [dispatch]);

  if (!meeting || !session || !rooms || !selfUser)  return <LoadingView fullscreen message="Chargement en cours" />;

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER --------------------------------- //
  // ---------------------------------------------------------------------- //

  const renderDevSoundIndicator = () => (
    <div style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0, pointerEvents: "none" }}>
      <DevSoundIndicatorsBanner
        meetingData={populated}
        canHearSession={(session) => canHearSession(containerAPI, session)}
      />
    </div>
  );

  const renderMobileWorkBoxOverlay = () => {
    if (!isMobile) return null;
    return (
      <OverlayContainer
        visible={workBoxOpen}
        onClose={closeWorkBoxOverlay}
        overlayStyle={{ padding: "0.5em", gap: "0.5em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <>
            <div className={styles.mobileWorkBoxTabs} onClick={(e) => e.stopPropagation()}>
              {mobileWorkBoxTabs.map((tab) => (
                <PrimaryButton
                  key={tab.mode}
                  className={clsx(styles.mobileWorkBoxTab, {
                    [styles.mobileWorkBoxTabActive]: workBoxMode === tab.mode,
                  })}
                  onClick={() => handleMobileTabClick(tab.mode)}
                >
                  {tab.notificationCount !== undefined && tab.notificationCount > 0 && workBoxMode !== tab.mode && (
                    <div className={styles.mobileWorkBoxTabBadge}>{tab.notificationCount}</div>
                  )}
                  {tab.icon}
                </PrimaryButton>
              ))}
            </div>
            <div
              className={styles.mobileWorkBoxOverlay}
              onClick={(e) => e.stopPropagation()}
            >
              <CurrentWorkBox chatInfo={chatInfo} onClose={closeWorkBoxOverlay} />
            </div>
          </>
        }
      />
    );
  };

  const renderAlwaysMountedSourcesOverlay = () => {
    const highlightMode = highlightMediaSelection.mode;
    const isHighlightMode = highlightMode !== "none";
    const isVisible = !!sourcePickerOpen || isHighlightMode;

    // Requesting: clicking outside cancels the request
    // Confirming: cannot close by clicking outside
    // Normal: close the source picker
    const handleClose = highlightMode === "requesting"
      ? highlightMediaSelection.onCancel
      : highlightMode === "confirming"
      ? undefined
      : closeSourcesOverlay;

    const highlightFooter = isHighlightMode ? (
      <div className={styles.highlightFooter}>
        {highlightMode === "confirming" && highlightMediaSelection.remainingSeconds !== undefined && highlightMediaSelection.remainingSeconds > 0 && (
          <span className={styles.highlightTimer}>
            Confirmation automatique dans {highlightMediaSelection.remainingSeconds}s
          </span>
        )}
        <MediaGlobalControls sources={usableSources} />
        <div className={styles.highlightActions}>
          <Button.Simple
            label={highlightMode === "requesting" ? "Annuler" : "Refuser"}
            onClick={highlightMediaSelection.onCancel}
            className={clsx(styles.highlightPopupBtn, styles.cancel)}
          />
          <Button.Simple
            label={highlightMode === "requesting" ? "Confirmer et demander" : "Confirmer et intervenir"}
            onClick={highlightMediaSelection.onConfirm}
            className={styles.highlightPopupBtn}
          />
        </div>
      </div>
    ) : undefined;

    const title = highlightMode === "requesting"
      ? "Monter au tableau"
      : highlightMode === "confirming"
      ? "Intervention — Définissez vos sources"
      : undefined;

    return (
      <OverlayContainer
        visible={isVisible}
        unmountWhenInvisible={false}
        onClose={handleClose}
        overlayStyle={{ padding: "0.5em", gap: "0.5em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <div
            className={styles.sourcesOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <MediaSourceWorkBox
              title={title}
              footer={highlightFooter}
              onClose={handleClose}
            >
              <MediaSourcePicker
                meetingAPI={containerAPI}
                requireBackendMedia={!!session?.medias?.length}
                sources={usableSources}
                display
                // Like the other picker usages (lobby, list, config popup): a
                // gesture/autoplay error while configuring must not toast nor
                // touch the toggles.
                ignoreGestureError
                style={{ width: "100%", height: "100%" }}
              />
            </MediaSourceWorkBox>
          </div>
        }
      />
    );
  };

  const renderShareOverlay = () => {
    return (
      <OverlayContainer
        visible={shareOpen}
        onClose={closeShareOverlay}
        overlayStyle={{ padding: "0.5em", gap: "0.5em" }}
        style={{ position: "absolute", inset: 0, zIndex: 10 }}
        overlay={
          <div
            className={styles.shareOverlay}
            onClick={(e) => e.stopPropagation()}
          >
            <ShareWorkBox onClose={closeShareOverlay} />
          </div>
        }
      />
    );
  };

  const renderUI = (children: React.ReactNode) => {
    if (isMobile) {
      return (
        <div className={clsx(styles.uiContainer, styles.uiContainerMobile)}>
          <div style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}>
            <SurveyBar />
            <div style={{
              height: "100%",
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              padding: "0.5em",
              gap: "0.5em",
            }}>
              <div style={{
                width: "100%",
                flex: 1,
                minHeight: 0,
                position: "relative",
              }}>
                {children}
                {renderDevSoundIndicator()}
              </div>
              <BottomBar
                onUpdateMeetingState={updateMeetingState}
                onPause={onPause}
                onStop={onStop}
                sources={usableSources}
                showLayoutSelector={enabledMedia.length > 1}
                onOpenSources={openSourcesOverlay}
                sourcesActive={sourcePickerOpen}
                notificationCount={(unreadMessagesNb || 0) + (populated.highlighted.requesting.length || 0) || undefined}
                onRequestHighlight={highlightMediaSelection.startRequest}
                screenShare={screenShare}
              />
            </div>
          </div>
          {renderMobileWorkBoxOverlay()}
        </div>
      );
    }

    return (
      <div className={clsx({
        [styles.uiContainer]: true,
        [styles.boxOpen]: workBoxOpen,
      })}>
        <SideBar
          chatAdapter={chatInfo?.adapter}
          highlightRequestsNb={populated.highlighted.requesting.length}
        />
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}>
          <SurveyBar />
          <div style={{
            height: "100%",
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            padding: "1em 1em 1em 0em",
            gap: "1em",
          }}>
            <div style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
            }}>
              <div className={styles.workContainer}>
                <RevealContainer
                  open={workBoxOpen}
                  gap="1em"
                  duration={500}
                  easing="cubic-bezier(.4, 0, .2, 1)"
                  className={styles.wbRevealContainer}
                >
                  <CurrentWorkBox
                    chatInfo={chatInfo}
                  />
                </RevealContainer>
                <div className={styles.childrenContainer}>
                  {children}
                  {renderDevSoundIndicator()}
                </div>
              </div>
            </div>
            <BottomBar
              onUpdateMeetingState={updateMeetingState}
              onPause={onPause}
              onStop={onStop}
              sources={usableSources}
              showLayoutSelector={enabledMedia.length > 1}
              onRequestHighlight={highlightMediaSelection.startRequest}
              screenShare={screenShare}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderDebugOutgoingStream = () => {
    if (!settings?.debug?.displayOutgoingStreamStats && !settings?.debug?.displaySessionIds) return null;
    return (
      <BottomPanelWebrtcSessionStats
        incomingSessions={settings?.debug?.displayIncomingStreamStats ? receptionSessions : undefined}
        outgoingSessions={settings?.debug?.displayOutgoingStreamStats ? emissionSessions : undefined}
        sessions={(emissionSessions ?? []).map((s, i) => ({
          id: s.getId(),
          name: `${s.data?.mediaSession?.name ?? `ouput n°${i}`}`,
          session: settings?.debug?.displayOutgoingStreamStats ? s : undefined,
          meeting: settings?.debug?.displaySessionIds ? { session: session, mediaSession: s.data?.mediaSession } : undefined,
        }))}
      />
    );
  };

  return (
    <MasterUI>
      <PauseView
        onResume={onResume}
        enabled={isExpert(session)}
        meetingPaused={meeting?.state === MeetingState.PAUSED}
        meetingPausedAt={meeting?.pausedAt}
      >
        <div className={styles.mainContainer}>
          {renderUI(<Outlet />)}
          {renderDebugOutgoingStream()}
          <HighlightPopup />
          <VideoSignalAutoHide meetingAPI={containerAPI} />
          {renderAlwaysMountedSourcesOverlay()}
          {renderShareOverlay()}
        </div>
      </PauseView>
    </MasterUI>
  );
};
